import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "../../dashboard/data/reportsLineChartData";
import Papa from 'papaparse';

function CnpTransactions() {
  const { cnpTransactionsReportsColumns } = data();
  const { predefinedRanges } = reportsLineChartData;
  const [transactions, setTransactions] = useState([]);

  // Function to get today's date at midnight in AEST timezone
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of the day in local time
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000); // Convert to local midnight in AEST
  };

  // Initialize lastMonthDateRange with today's date at midnight as both start and end dates
  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    getTodayDateAtMidnight().toISOString().slice(0, 10),
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  ]);

  const api = useApi();

  // Format date to 'yyyy-MM-dd' and adjust to AEST timezone
  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      // Handle cases where date is not a valid Date object
      console.error("Invalid date provided:", date);
      return '';
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const getTransactions = (dateRange) => {
    if (!dateRange || dateRange.length !== 2) return;

    const fromDate = new Date(dateRange[0]);
    const toDate = new Date(dateRange[1]);

    const data = {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate)
    };
    
    api.post("reports/getCnpTransactionsSummary", data)
      .then((response) => {
        if (response.data.statusCodeValue === 200) {
          setTransactions(response.data.body);
        }
      })
      .catch((error) => {
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        const toastId = toast.error("No Record Found", { autoClose: false }); // Prevents auto-closing
        setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      });
  };

  const generateCSV = () => {
    if (transactions.length === 0) {
      return '';
    }

    const filteredColumns = cardTransactionsReportsColumns.filter(column => 
      column.Header !== "Action" && column.Header !== "Settlement Allowed"
    );
    const headers = filteredColumns.map(column => column.Header);

    const transactionsWithFilteredColumns = transactions.map(transaction => {
      return filteredColumns.map(column => {
        const accessor = column.accessor;
        const value = typeof accessor === 'function' ? accessor(transaction) : transaction[accessor];
        return value;
      });
    });

    const csvData = [headers, ...transactionsWithFilteredColumns];

    const csv = Papa.unparse(csvData, {
      quotes: true,
    });

    return csv;
  };

  const handleDownloadCSV = () => {
    if (transactions.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false }); // Prevents auto-closing
      setTimeout(() => toast.dismiss(toastId), 1000); // Dismisses the toast after 1 second
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `CardTransactions_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    getTransactions(lastMonthDateRange);
  }, [lastMonthDateRange]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={12}>
            <Grid item xs={12} md={12} lg={12}>
              <MDBox mb={3} textAlign={"center"}>
                <DateRangePicker
                  disabledDate={afterToday()}
                  ranges={predefinedRanges}
                  onChange={(dateRange) => {
                    if (dateRange.length === 2) {
                      setLastMonthDateRange(dateRange);
                    }
                  }}
                  format="yyyy-MM-dd"
                  value={
                    lastMonthDateRange.length === 2 
                      ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] 
                      : []
                  }
                />
              </MDBox>
            </Grid>
            <MDButton
              variant="gradient"
              onClick={handleDownloadCSV}
              color="info"
              style={{ marginRight: "24px", marginBottom: "12px" }}
            >
              Export
            </MDButton>
            <DataTable
              table={{ columns: cnpTransactionsReportsColumns, rows: transactions }}
              showTotalEntries={true}
              isSorted={true}
              noEndBorder
              entriesPerPage={true}
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default CnpTransactions;
